import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Container, FormDateField, FormNumberField, FormikWrapper, Button, Message,
  FormCheckbox,
} from '@hvcgroep/design-system';
import Warmte from '@hvcgroep/design-system/assets/illustrations/Warmte.svg';
import { useDispatch } from 'react-redux';
import useContract from '../../hooks/useContract';
import ErrorPage from '../ErrorPage';
import SkeletonLoader from '../../components/SkeletonLoader';
import Faqs from '../../containers/Faqs';
import { FAQ_CATEGORIES } from '../../config/constants';

import './style.scss';
import { api, useGetMeterReadTasksQuery, usePostMeterReadsMutation } from '../../store/api-generated';
import CustomButtonLinkWrapper from '../../components/CustomButtonLinkWrapper';
import PageHeader from '../../components/PageHeader';

const SubmitMeterReadings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const heading = t('pages.usage.submitMeterReadings');
  const siteTitle = t('general.siteTitle');

  const {
    selectedContract,
    selectedCustomer,
    selectedAccount,
    isLoading: isContractLoading,
    isError: isContractError,
  } = useContract();

  const [submit,
    {
      isLoading: isSubmitLoading,
      isSuccess: isSubmitSuccess,
      isError: isSubmitError,
    }] = usePostMeterReadsMutation();

  const {
    data: meterReadTasks,
    isLoading: isGetMeterReadTaskLoading,
    isError: isGetMeterReadTaskError,
  } = useGetMeterReadTasksQuery({
    taskId: String(selectedContract?.taskId!),
  }, {
    skip: !selectedContract?.taskId,
  });

  const hasTaskId = !!selectedContract?.taskId;

  const meterReadTask = meterReadTasks?.[0];

  const isLoading = isContractLoading || isGetMeterReadTaskLoading;

  if (isContractError || isGetMeterReadTaskError) {
    return <ErrorPage type="serverError" />;
  }

  const initialValues = {
    date: new Date().toISOString().split('T')[0],
    meterReading: undefined,
    acceptedWarning: false,
  } as { date: string, meterReading: undefined | number, acceptedWarning: boolean };

  const validationSchema = Yup.object().shape({
    date: Yup.date()
      .required(t('pages.submitMeterReadings.form.date.error.required')),

    meterReading: Yup.number()
      .typeError(t('pages.submitMeterReadings.form.meterReading.error.required'))
      .required(t('pages.submitMeterReadings.form.meterReading.error.required')),

    acceptedWarning: Yup.boolean()
      .when('meterReading', {
        is: (currentMeterValue: number) => {
          const min = meterReadTask?.expectedReadMin!;
          const max = meterReadTask?.expectedReadMax!;
          return currentMeterValue && (currentMeterValue < min || currentMeterValue > max);
        },
        then: (schema) => schema.required(t('pages.submitMeterReadings.form.acceptedWarning.error.required'))
          .oneOf([true], t('pages.submitMeterReadings.form.acceptedWarning.error.required')),
        otherwise: (schema) => schema,
      }),
  });

  const onSubmit = async (values: typeof initialValues) => {
    const contractId = selectedContract?.id;

    if (!contractId) {
      return;
    }
    const payload = {
      meterReads: {
        alternateId: selectedCustomer?.alternateId!,
        accountId: meterReadTask?.accountId!,
        ldcAccount: meterReadTask?.ldcAccountId!,
        taskId: String(meterReadTask?.taskId),
        meterNo: meterReadTask?.meterNumber!,
        date: values.date,
        meterRead: String(values.meterReading!),
      },
    };

    try {
      await submit(payload).unwrap();

      dispatch(api.util.updateQueryData('getCustomerDetails', {}, (draft) => {
        const customer = draft.find((customerItem) => customerItem.alternateId === selectedCustomer!.alternateId);
        const account = customer?.accounts?.find((accountItem) => accountItem.id === selectedAccount?.id);
        const contract = account?.contracts?.find((contractItem) => contractItem.id === selectedContract?.id);

        if (contract) {
          delete contract.taskId;
        }
      }));
      // Redirect with success status
      history.push('/meterstanden', { submitStatus: 'success' });
    } catch (error) {
      console.error('Submission failed', error);
      // Redirect with error status
      history.push('/meterstanden', { submitStatus: 'error' });
    }
  };

  return (
    <Container fluid>
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

      {isLoading && (
        <SkeletonLoader type="general" />
      )}

      {!isLoading && (
        <>
          <PageHeader heading={heading} />
          <div className="row">
            <div className="col-md-6 u-margin--bottom--6">
              {!hasTaskId && (
                <>
                  <Message>
                    {t('pages.submitMeterReadings.noReadingRequired')}
                  </Message>
                  <Button variation="center" type="secondary" href="/" CustomLinkComponent={CustomButtonLinkWrapper}>
                    {t('pages.submitMeterReadings.toOverview')}
                  </Button>
                </>
              )}
              {meterReadTask && hasTaskId && (
                <FormikWrapper
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  validateOnChange={false}
                  validateOnBlur={false}
                  onSubmit={onSubmit}
                >
                  {(formik) => {
                    const min = meterReadTask?.expectedReadMin!;
                    const max = meterReadTask?.expectedReadMax!;
                    const errorMessage = t('pages.submitMeterReadings.form.meterReading.error.range', {
                      min: min?.toFixed(3).replace('.', ','),
                      max: max?.toFixed(3).replace('.', ','),
                    });

                    const currentMeterValue = formik.values.meterReading;
                    const isOutOfRange = currentMeterValue && (currentMeterValue < min || currentMeterValue > max);

                    return (
                      <form onSubmit={formik.handleSubmit}>
                        <div className="mo-submit-meter-readings o-background--grey">
                          <div className="mo-submit-meter-readings__header">
                            <img src={Warmte} alt="" />
                            <h3>{t('pages.submitMeterReadings.warmte')}</h3>
                          </div>

                          <FormDateField
                            isLiveValidationDisabled
                            name="date"
                            label={t('pages.submitMeterReadings.form.date.label')}
                          />

                          <FormNumberField
                            name="meterReading"
                            isLiveValidationDisabled
                            label={t('pages.submitMeterReadings.form.meterReading.label')}
                            description={t('pages.submitMeterReadings.form.meterReading.description')}
                            accuracy={3}
                            allowDecimals
                            unit={t('pages.submitMeterReadings.form.meterReading.unit')}
                            unitAfter
                            placeholder="0000,000"
                          />

                          <p className="mo-submit-meter-readings__task">
                            <Trans
                              i18nKey="pages.submitMeterReadings.form.previousReading"
                              values={{
                                date: new Intl.DateTimeFormat(document.documentElement.lang, {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: 'numeric',
                                }).format(new Date(
                                  meterReadTask.previousReadDate ?? '',
                                )),
                                reading: meterReadTask.previousRead?.toFixed(3).toString().replace('.', ','),
                              }}
                            >
                              <strong />
                            </Trans>
                          </p>

                          { isOutOfRange && formik.touched.meterReading && (
                            <>
                              { !formik.values.acceptedWarning && (
                                <Message type="warning">
                                  {errorMessage}
                                </Message>
                              )}

                              <FormCheckbox
                                name="acceptedWarning"
                                label={t('pages.submitMeterReadings.form.acceptedWarning.error.checkbox')}
                              />
                            </>
                          )}

                        </div>

                        <Button
                          htmlType="submit"
                          variation="center"
                          isLoading={isSubmitLoading}
                        >
                          {t('pages.submitMeterReadings.form.submit')}
                        </Button>
                      </form>
                    );
                  }}
                </FormikWrapper>

              )}

            </div>
            <div className="col-md-6">
              <h3>{t('pages.submitMeterReadings.faqTitle')}</h3>
              <Faqs categoryId={FAQ_CATEGORIES.METERSTANDEN} />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default SubmitMeterReadings;
